import { AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

/**
 * EmptyState - Inline empty state for sections, panels, tabs, modals
 *
 * Passive states (no action): All text is gray-500
 * Active states (with action): All text is gray-900
 *
 * For page-level empty states, use PageEmptyState instead.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className = '',
}: EmptyStateProps) {
  // Use gray-500 for passive states (no action), gray-900 for active states (has action)
  const textColor = action ? 'text-gray-900' : 'text-gray-500';

  return (
    <Card className={`rounded-lg border bg-white ${className}`}>
      <CardContent className="p-6">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          {icon && <div className="mb-4">{icon}</div>}
          <h3 className={`text-lg font-semibold ${textColor} mb-2`}>{title}</h3>
          <p className={`${textColor}${action ? ' mb-4' : ''}`}>{description}</p>
          {action}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * PageEmptyState - Full-page empty state with centered content
 *
 * Use this for page-level empty states where the entire page has no content.
 * Centers content vertically with min-height of 66vh.
 *
 * Passive states (no action): All text is gray-500
 * Active states (with action): All text is gray-900
 */
export function PageEmptyState({
  icon,
  title,
  description,
  action,
  className = '',
}: EmptyStateProps) {
  // Use gray-500 for passive states (no action), gray-900 for active states (has action)
  const textColor = action ? 'text-gray-900' : 'text-gray-500';

  return (
    <Card className={`rounded-lg border bg-white min-h-[66vh] flex items-center justify-center ${className}`}>
      <CardContent className="p-6 w-full">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          {icon && <div className="mb-4">{icon}</div>}
          <h3 className={`text-lg font-semibold ${textColor} mb-2`}>{title}</h3>
          <p className={`${textColor}${action ? ' mb-4' : ''}`}>{description}</p>
          {action}
        </div>
      </CardContent>
    </Card>
  );
}

interface PageErrorStateProps {
  title?: string;
  description?: React.ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

/**
 * PageErrorState - Full-page load-failure state.
 *
 * Use when a page's primary data fails to load (4xx/5xx). This is deliberately
 * distinct from PageEmptyState ("nothing here yet") so a load error never poses
 * as missing data, which would mislead the user into thinking they have no
 * campaigns/business/reports when the request actually failed.
 *
 * Red AlertCircle (semantic error #B91C1C) + optional Retry button. Wraps
 * PageEmptyState so layout and spacing stay identical to the empty state.
 */
export function PageErrorState({
  title = 'Something Went Wrong',
  description = 'This page failed to load. This is a loading error, not missing data, so nothing has been lost. Retry, or contact support if it persists.',
  onRetry,
  retryLabel = 'RETRY',
  className = '',
}: PageErrorStateProps) {
  return (
    <PageEmptyState
      className={className}
      icon={<AlertCircle className="h-12 w-12 text-[#B91C1C]" strokeWidth={1} />}
      title={title}
      description={description}
      action={
        onRetry ? (
          <Button
            onClick={onRetry}
            size="lg"
            className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
          >
            <span>{retryLabel}</span>
          </Button>
        ) : undefined
      }
    />
  );
}
